import {
    DoubleValidator,
    IntValidator,
    StringValidator,
} from '@kcftech/prometheus-client';
import {
    AssemblyParameterElementDto,
    AssemblyParameterFormData,
    getParameterType,
} from 'pages/Settings/AssemblyType/parameter_form/types';
import { useForm } from 'react-hook-form';
import { ParameterType } from 'utils/assembly_application';

const extractFormValuesFromResponse = (
    existingParameter: AssemblyParameterElementDto,
): AssemblyParameterFormData => {
    const paramType = getParameterType(existingParameter);
    return {
        name: existingParameter.FieldDisplayName,
        code: existingParameter.FieldKey,
        type: paramType,
        defaultValue: existingParameter.DefaultValue,
        ...((type: ParameterType) => {
            switch (type) {
                case 'integer': {
                    if (
                        existingParameter.Validator &&
                        !(existingParameter.Validator instanceof IntValidator)
                    ) {
                        throw new Error('Unexpected Validator Type');
                    }
                    return {
                        minValue: existingParameter.Validator?.Min?.Value,
                        maxValue: existingParameter.Validator?.Max?.Value,
                        minValueInclusive: true,
                        maxValueInclusive: true,
                    };
                }
                case 'decimal': {
                    if (
                        existingParameter.Validator &&
                        !(existingParameter.Validator instanceof DoubleValidator)
                    ) {
                        throw new Error('Unexpected Validator Type');
                    }
                    return {
                        minValue: existingParameter.Validator?.Min?.Value,
                        maxValue: existingParameter.Validator?.Max?.Value,
                        minValueInclusive:
                            existingParameter.Validator?.Min?.Exclusive !== undefined
                                ? !existingParameter.Validator.Min.Exclusive
                                : undefined,
                        maxValueInclusive:
                            existingParameter.Validator?.Max?.Exclusive !== undefined
                                ? !existingParameter.Validator.Max.Exclusive
                                : undefined,
                    };
                }
                case 'selection': {
                    if (
                        existingParameter.Validator &&
                        !(existingParameter.Validator instanceof StringValidator)
                    ) {
                        throw new Error('Unexpected Validator Type');
                    }
                    return {
                        validValues: existingParameter.Validator?.ValidValues,
                    };
                }
                case 'text': {
                    return {};
                }
                case 'bool': {
                    return { defaultValue: existingParameter.DefaultValue ?? false };
                }
                default:
                    throw new Error('Unexpected parameter type');
            }
        })(paramType),
    } as AssemblyParameterFormData;
};

export const useAssemblyParameterForm = (
    existingParameter?: AssemblyParameterElementDto,
) =>
    useForm<AssemblyParameterFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: existingParameter
            ? extractFormValuesFromResponse(existingParameter)
            : {
                  name: '',
                  code: '',
                  type: 'text',
                  defaultValue: undefined,
              },
    });
